import { useState, useEffect, useRef, useCallback } from 'react';
import { Speech } from 'types/voiceSearch';
import { API_ERROR, BLOCK_MICROPHONE, CURRENT_LANG, defaultSpeech, LISTENING, USER_INACTIVITY, USER_INACTIVITY_THRESHOLD, VOICE_INACTIVITY, VOICE_SESSION_THRESHOLD } from 'constants/voiceSearch';
//handle close status (reason)
// 0 listening state
// 1 voice session cut after VOICE_SESSION_THRESHOLD / TEXT IS PRESENT
// 2  user inactivity after USER_INACTIVITY_THRESHOLD
// 3  audio end by web speech api
// 4  other error browser not supported / block microphone

const useMicrophone = () => {
  const [searchFinalString, setSearchFinalString] = useState('');
  const [searchInterString, setSearchInterString] = useState('');
  const finalString = useRef('');
  const [voiceStatus, setVoiceStatus] = useState(-1);
  const [support, setSupport] = useState(false);
  const voiceSearchSupport = useRef(false);
  const recognition = useRef<Speech>(defaultSpeech);
  const timeoutId = setTimeout(() => '', 0);
  const voiceTimeOut = useRef(timeoutId);
  const userTimeOut = useRef(timeoutId);
  const stopListen = useCallback((reason: number = API_ERROR) => {
    if (!voiceSearchSupport.current) {
      return;
    }
    if (finalString.current) {
      reason = 1;
      // if text exist then search the text
    }
    if (recognition && recognition.current) {
      recognition.current.stop();
      setSearchFinalString('');
      setSearchInterString('');
    }
    if (voiceTimeOut.current) {
      clearTimeout(voiceTimeOut.current);
    }
    if (userTimeOut) {
      clearTimeout(userTimeOut.current);
    }
   setVoiceStatus(reason);
  }, []);

  const startListen = useCallback(() => {
    if (!voiceSearchSupport.current) {
      return;
    }
    if (recognition && recognition.current) {
      recognition.current.start();
    }
    else {
      stopListen(API_ERROR);
    }
  }, [stopListen]);

  useEffect(() => {
    if (!('webkitSpeechRecognition' in window)) {
      setSupport(false);
      return;
    }
    setSupport(true);
    voiceSearchSupport.current = true;
    recognition.current = new window.webkitSpeechRecognition();
    recognition.current.continuous = true;
    recognition.current.lang = CURRENT_LANG;
    recognition.current.interimResults = true;
    recognition.current.onresult = function(e) {
      if (voiceTimeOut.current) {
        clearTimeout(voiceTimeOut.current);
      }
      if (userTimeOut) {
        clearTimeout(userTimeOut.current);
      }
       voiceTimeOut.current = setTimeout(() => {
            stopListen(VOICE_INACTIVITY);
            clearTimeout(voiceTimeOut.current);
      }, VOICE_SESSION_THRESHOLD);
      let final = '';
      let interim = '';
      for (let i = e?.resultIndex; i < e?.results?.length; i++) {
        if (e?.results?.[i]?.[0]) {
          const transcript = e.results[i][0]?.transcript || '';
          if (e.results[i].isFinal) {
            final += transcript;
          } else {
            interim += transcript;
          }
        }
      }
      if (final) {
        setSearchFinalString(final);
        finalString.current = final;
        setSearchInterString('');
      } else {
        finalString.current = interim;
        setSearchInterString(interim);
      }
    };
    recognition.current.onstart = function() {
      setVoiceStatus(LISTENING);
      userTimeOut.current = setTimeout(() => {
        const isOut = (!searchFinalString);
        if (isOut) {
          stopListen(USER_INACTIVITY);
          clearTimeout(userTimeOut.current);
        }
    }, USER_INACTIVITY_THRESHOLD);
    };
    recognition.current.onsoundend = function() {
      stopListen(API_ERROR);
    };
    recognition.current.onaudioend = function() {
      stopListen(API_ERROR);
    };
    recognition.current.onspeechend = function() {
      stopListen(API_ERROR);
    };
    recognition.current.onerror = function() {
      stopListen(BLOCK_MICROPHONE);
    };
    recognition.current.onstop = () => {
      stopListen(API_ERROR);
    };
  }, [searchFinalString, stopListen]);
  return {
    support,
    startListen: startListen,
    final: searchFinalString,
    interim: searchInterString,
    stopListen: stopListen,
    voiceStatus,
  };
};

export default useMicrophone;
